import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Add } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Slide from '@mui/material/Slide';
import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';

const Transition = React.forwardRef((props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
));

const getSelectedAnswerKey = (item) => {
    if (!item?.answer) return '';
    if (['option1', 'option2', 'option3', 'option4'].includes(item.answer)) {
        return item.answer;
    }
    const optKey = Object.keys(item?.options || {}).find(
        (key) =>
            item.options[key]?.trim().toLowerCase() ===
            item.answer.trim().toLowerCase()
    );
    return optKey || '';
};

const QuizAssessmentDialog = ({
    showQuestionForm,
    handleCreateQuestionsClose,
    formData,
    handleChange,
    addQuestion,
    handleSubmitForm,
    theme,
    deleteQuestion,
    disableSubmitAssessment,
    setDisableSubmitAssessment
}) => (
    <Dialog
        open={showQuestionForm}
        onClose={handleCreateQuestionsClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
            sx: {
                borderRadius: '16px',
                overflow: 'hidden'
            }
        }}
    >
        <DialogTitle
            sx={{
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
            }}
        >
            <AssignmentIcon sx={{ color: theme.palette.secondary.main }} />
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Add Video Assessment
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'rgba(255,255,255,0.7)',
                        display: 'block'
                    }}
                >
                    Create interactive timestamped questions for this video
                    lecture.
                </Typography>
            </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4, mt: 2 }}>
            <Box my={1}>
                <Stack spacing={4}>
                    {formData?.questions?.map((currentItem, index) => (
                        <Box
                            key={currentItem?._id || index}
                            sx={{
                                p: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                borderLeft: `8px solid ${theme.palette.secondary.main}`,
                                borderRadius: '12px',
                                boxShadow: theme.shadows[1],
                                backgroundColor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 700,
                                        color: theme.palette.primary.main
                                    }}
                                >
                                    Question {index + 1}
                                </Typography>
                                <IconButton
                                    onClick={() => deleteQuestion(index)}
                                    disabled={formData?.questions?.length <= 1}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <TextField
                                fullWidth
                                label="Question Text"
                                name="question"
                                variant="outlined"
                                value={currentItem?.question}
                                onChange={(e) => handleChange(e, index)}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Option A"
                                        name="option1"
                                        value={currentItem?.options?.option1}
                                        onChange={(e) => handleChange(e, index)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Option B"
                                        name="option2"
                                        value={currentItem?.options?.option2}
                                        onChange={(e) => handleChange(e, index)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Option C"
                                        name="option3"
                                        value={currentItem?.options?.option3}
                                        onChange={(e) => handleChange(e, index)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Option D"
                                        name="option4"
                                        value={currentItem?.options?.option4}
                                        onChange={(e) => handleChange(e, index)}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                select
                                fullWidth
                                label="Correct Answer"
                                name="answer"
                                value={getSelectedAnswerKey(currentItem)}
                                onChange={(e) => {
                                    handleChange(e, index);
                                    setDisableSubmitAssessment(
                                        e.target.value === ''
                                    );
                                }}
                            >
                                <MenuItem value="option1">
                                    Option A:{' '}
                                    {currentItem?.options?.option1 || ''}
                                </MenuItem>
                                <MenuItem value="option2">
                                    Option B:{' '}
                                    {currentItem?.options?.option2 || ''}
                                </MenuItem>
                                <MenuItem value="option3">
                                    Option C:{' '}
                                    {currentItem?.options?.option3 || ''}
                                </MenuItem>
                                <MenuItem value="option4">
                                    Option D:{' '}
                                    {currentItem?.options?.option4 || ''}
                                </MenuItem>
                            </TextField>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </DialogContent>

        <Divider />

        <DialogActions
            sx={{
                p: 3,
                display: 'flex',
                justifyContent: 'space-between'
            }}
        >
            <Button
                color="secondary"
                variant="outlined"
                startIcon={<Add />}
                onClick={addQuestion}
                sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 'bold'
                }}
            >
                Add Another Question
            </Button>

            <Box display="flex" gap={2}>
                <Button
                    color="inherit"
                    variant="outlined"
                    onClick={handleCreateQuestionsClose}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 'bold'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleSubmitForm}
                    disabled={disableSubmitAssessment}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 'bold'
                    }}
                >
                    Submit Quiz
                </Button>
            </Box>
        </DialogActions>
    </Dialog>
);

export default QuizAssessmentDialog;
